<template>
  <div v-bind:key="refreshKey">
    <slot name="table-top-actions" v-bind:selectedIds="selectedIds"></slot>
    <b-table responsive v-handy-scroll v-bind:items="rows" v-bind:fields="fields" no-local-sorting
             v-bind:sort-by.sync="sort.field" v-bind:sort-desc.sync="sortIsDesc"
             striped hover @row-clicked="onRowClicked" v-bind:busy="loading"
             @sort-changed="onSortChanged"
             style="white-space: nowrap; padding-bottom: 30px"
    >
      <template #head(selection)="data">
        <b-form-checkbox v-bind:indeterminate="isIndeterminate(selected)" v-model="selected.all"
                         style="margin-top: 7px"
                         @input="val=>toggleAllRow(val)" inline
        ></b-form-checkbox>
      </template>

      <template #cell(orgId)="data">
        {{ getOrganizationNameById(data.value) }}
      </template>
      <!-- Generic slot for dropdown fields -->
      <template #cell(status)="data">
        <b-badge :variant="getBadgeColor('status', data.item.status)">
          {{ data.item.status }}
        </b-badge>
      </template>

      <template #cell(importance)="data">
        <b-badge :variant="getBadgeColor('importance', data.item.importance)">
          {{ data.item.importance }}
        </b-badge>
      </template>

      <template #cell(priority)="data">
        <b-badge :variant="getBadgeColor('priority', data.item.priority)">
          {{ data.item.priority }}
        </b-badge>
      </template>
      <template #cell(dailySpend)="data">
        {{ convertToUSD(data.value) }}
      </template>

      <template #cell(facebookOrDrive)="data">
        <b-badge :variant="getBadgeColor('facebookOrDrive', data.item.facebookOrDrive)">
          {{ data.item.facebookOrDrive }}
        </b-badge>
      </template>


      <template #cell(trackingLink)="data">
        <div class="d-flex align-items-center">
          <!-- Clickable link -->
          <b-link
              :href="data.item.trackingLink"
              target="_blank"
              class="mr-2 text-primary"
          >
            Open
          </b-link>

          <!-- Copy button -->
          <b-button
              size="sm"
              variant="outline-secondary"
              @click="copyToClipboard(data.item.trackingLink)"
              v-b-tooltip.hover
              title="Copy to clipboard"
          >
            <i class="fas fa-copy"></i>
          </b-button>
        </div>
      </template>
      <template #cell(catalogLink)="data">
        <div class="d-flex align-items-center">
          <!-- Clickable link -->
          <b-link
              :href="data.item.catalogLink"
              target="_blank"
              class="mr-2 text-primary"
          >
            Open
          </b-link>

          <!-- Copy button -->
          <b-button
              size="sm"
              variant="outline-secondary"
              @click="copyToClipboard(data.item.catalogLink)"
              v-b-tooltip.hover
              title="Copy to clipboard"
          >
            <i class="fas fa-copy"></i>
          </b-button>
        </div>
      </template>


      <template #cell(actions)="data">
        <div class="btn-group">


          <!-- Play / Pause -->
          <b-button
              v-if="data.item.isPlaying"
              class="btn btn-success mr-1"
              size="sm"
              @click="togglePlayPause(data.item,'stop')"
              v-b-tooltip.hover
              :title="'Pause'"
          >
            <i :class="'fa fa-pause'"></i>
          </b-button>
          <b-button
              v-else
              class="btn btn-primary mr-1"
              size="sm"
              @click="togglePlayPause(data.item,'start')"
              v-b-tooltip.hover
              :title="'Play'"
          >
            <i :class="'fa fa-play'"></i>
          </b-button>

          <!-- Google Drive Link -->
          <b-button
              v-if="data.item.facebookOrDrive=== 'Drive'"
              variant="info"
              size="sm"
              :href="data.item.catalogLink"
              target="_blank"
              v-b-tooltip.hover
              title="Open in Google Drive"
          >
            <i class="fab fa-google-drive"></i>
          </b-button>
          <!-- Facebook Link -->
          <b-button
              v-else-if="data.item.facebookOrDrive === 'Direct Facebook'"
              variant="primary"
              size="sm"
              :href="data.item.catalogLink"
              target="_blank"
              v-b-tooltip.hover
              title="Open in Facebook"
          >
            <i class="fab fa-facebook"></i>
          </b-button>

        </div>
      </template>


      <slot name="table-body" v-bind:rows="rows"></slot>

    </b-table>
    <!--{{pagination}}-->
    <div class="mt-2 text-center">
      <div class="d-inline-block">
        <b-pagination
            v-model="pagination.page"
            v-bind:total-rows="pagination.total"
            v-bind:per-page="pagination.size"
            @page-click="onPageClick"
            align="center"
        ></b-pagination>
      </div>
      <div class="d-inline-block">
        <b-select class="d-inline-block" style="width: 70px" size="sm" v-model="pagination.size" @change="refresh">

          <b-select-option v-bind:value="25">25</b-select-option>
          <b-select-option v-bind:value="50">50</b-select-option>
          <b-select-option v-bind:value="100">100</b-select-option>
        </b-select>
        /{{ pagination.total }} {{ $t('columns.counterNames.leads') }}

      </div>

    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinTable from '@/components/mixin/mixinTable';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';

export default {
  name: 'LeadsTable',
  mixins: [mixinBase, mixinTable, mixinNotifications],
  data: () => ({
    rows: [],
    selected: { all: false },
  }),
  props: {
    filters: {
      default: () => ({})
    },
    columns: {
      default: () => ([])
    },
    hasSelection: {
      default: () => false
    },
    lazy: {
      default: () => false
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.pagination.page = 1;
        this.refresh();
      }
    }
  },
  created() {
    this.setPagination(1, 0, 25);
    if (!this.lazy) {
      this.refresh();
    }
  },
  computed: {
    ...mapGetters('data', ['getUserById', 'getOrganizationNameById']),
    fields() {
      let $this = this;
      let fields = (this.columns || []).map(column => {
        return {
          key: column.key,
          label: $this.$t(`columns.${column.key}`),
          sortable: true,
          active: column.enable,
        };
      });
      if (this.hasSelection) {
        fields.unshift({
          key: 'selection',
          label: '',
          sortable: false,
          active: true,
        });
      }

      fields.push({
        key: 'actions',
        label: 'Actions',
        class: 'text-center',
        sortable: false,
        active: true,
      });

      return fields.filter(f => f.active === true);
    },
    selectedIds() {
      this.refreshKey; // Little hack to recompile
      // console.log("selectedIds()",this.selected)
      return Object.keys(this.selected)
          .filter(k => k !== 'all')
          .reduce((ans, id) => Object.assign(ans, { [id]: true }), {});
    },
  },
  methods: {
    ...mapActions('lead', ['getAllLeads','togglePlayingAction']),
    togglePlayPause(item,action) {
      let $this = this;
      let loadingToken = this.setLoading();
      this.togglePlayingAction({id:item.id,action:action})
          .then(() => {
            $this.showSuccessNotification($this.$t('globalMessages.success', { system: $this.$t('menu.leads') }));
          })
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.error', { system: $this.$t('menu.leads') }), $this.getErrorDescription(err));
          })
          .finally(() => {
            $this.setLoaded(loadingToken);
          });
    },
    getBadgeColor(type, value) {
      const maps = {
        status: {
          'Done': 'success',
          'Increase budget': 'success',
          'To cut': 'danger',
          'Check Facebook': 'primary',
          'To see': 'warning',
          'Add video': 'secondary',
          'Token error': 'danger',
          'To restart': 'info',
          'Keitaro OK': 'success',
          'Spam': 'danger',
          'Error': 'danger',
          'Creative to change': 'warning',
          'Restricted': 'dark',
          'Domain to change': 'danger',
          'Catalog to change': 'info',
          'Star': 'warning',
          'Request to do': 'secondary',
          'Tag on Dolphin': 'primary',
          'To Verify': 'warning',
          'Active': 'success',
          'Disabled': 'danger',
          'To Do': 'warning',
          'Product Created': 'success',
          'Blacklisted': 'dark',
          'Pause': 'secondary',
          'Account Lock': 'danger',
          'Active To Verify': 'warning',
          'Delete': 'danger',
          'Change Domain': 'danger',
          'To Activate': 'success',
          'Reactivate Pause': 'secondary',
          'Lead Error': 'danger',
          'Schedules': 'info',
          'Risk Control': 'danger'
        },

        importance: {
          'Done': 'success',
          'Increase budget': 'success',
          'To cut': 'danger',
          'Check Facebook': 'primary',
          'To see': 'warning',
          'Add video': 'secondary',
          'Token error': 'danger',
          'To restart': 'info',
          'Keitaro OK': 'success',
          'Spam': 'danger',
          'Error': 'danger',
          'Creative to change': 'warning',
          'Restricted': 'dark',
          'Domain to change': 'danger',
          'Catalog to change': 'info',
          'Star': 'warning',
          'Request to do': 'secondary',
          'Tag on Dolphin': 'primary'
        },

        priority: {
          'High': 'danger',
          'Medium': 'warning',
          'Low': 'success'
        },

        facebookOrDrive: {
          'Direct Facebook': 'primary',
          'Drive': 'info'
        }
      };

      return maps[type]?.[value] || 'light';
    },
    copyToClipboard(text) {
      if (!text) return;
      navigator.clipboard.writeText(text)
          .then(() => {
            this.$bvToast.toast('Link copied to clipboard!', {
              title: 'Copied',
              variant: 'success',
              solid: true,
              autoHideDelay: 2000
            });
          });
    },
    async refresh() {
      let $this = this;
      let loadingToken = this.setLoading();
      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'lastUpdate';
      }
      // console.log("refresh()", {  pagination: this.pagination, sort})
      const result = await this.getAllLeads(
          {
            pagination: this.pagination,
            sort,
            filter: this.filters,
          }
      )
          .catch(err => {
            $this.showErrorNotification($this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.leads') }), $this.getErrorDescription(err));
            $this.setLoaded(loadingToken);
          });
      console.log('refresh()', result);

      this.rows = result.data;
      this.setMetaDataFromResult(result);
      this.setLoaded(loadingToken);
    }
  }
};
</script>

<style scoped>

</style>
